import { useState } from 'react';
import { Alert } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ZealResponse, WorkersV2API } from '@/services/workersV2API';

type Props = {
  workerId: number;
  zeal: ZealResponse;
  onSync?: () => void;
};

const ZealCard = ({ workerId, zeal, onSync }: Props) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await WorkersV2API.syncFromZeal(workerId);
      if (result.success) {
        setSyncResult({
          success: true,
          message: 'Sync completed successfully',
        });
        onSync?.();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed',
        });
      }
    } catch (err) {
      setSyncResult({ success: false, message: 'Failed to sync from Zeal' });
    } finally {
      setSyncing(false);
    }
  };
  if (!zeal.available) {
    return (
      <Card title="Zeal">
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in zeal && zeal.reason
                ? zeal.reason
                : 'error' in zeal && zeal.error
                  ? zeal.error
                  : 'No Zeal data available for this worker'}
            </Small>
            {'employee_id' in zeal && zeal.employee_id && (
              <Small color="lighter">Employee ID: {zeal.employee_id}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card
      actions={[
        {
          label: syncing ? 'Syncing...' : 'Sync',
          onAction: handleSync,
          disabled: syncing,
        },
      ]}
      title="Zeal"
    >
      {syncResult && (
        <Card.Section>
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            onClose={() => setSyncResult(null)}
          >
            {syncResult.message}
          </Alert>
        </Card.Section>
      )}

      <Card.Section>
        <pre
          style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {JSON.stringify(zeal, null, 2)}
        </pre>
      </Card.Section>
    </Card>
  );
};

export default ZealCard;
